import { useEffect, type FC } from 'react'

import { InfoBox, Table } from '@skytech/ui'
import { Td } from '@skytech/ui/Table/Td'
import { Th } from '@skytech/ui/Table/Th'
import { getQueryFilter, getSortedList } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import AlertIcon from 'mdi-react/AlertIcon'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import Highlight from '@/components/Highlight'
import { NoResults } from '@/components/NoResults'
import { ResultCount } from '@/components/ResultCount'
import { useSearch, useSortParams } from '@/hooks/searchParams'
import { useFormattedNumberWithCurrency } from '@/hooks/use-formatted-number-with-currency'
import { useRenderLimit } from '@/hooks/use-render-limit'
import adminQueries from '@/modules/admin/queries'
import { useReportsContext } from '@/modules/admin/reports/index.context'
import { NorwayId } from '@/modules/admin/reports/ReportFilters'
import adminResources from '@/modules/admin/resources'
import type { ReportFilters, TaxAdvantageReport } from '@/modules/admin/resources'

export const TaxAdvantage: FC<{
  filters: ReportFilters
}> = ({ filters }) => {
  const { searchFilter } = useSearch()
  const { isReportListLoading, setCount, setIsReportListLoading } = useReportsContext()
  const { locale } = useAppContext()
  const [{ querySort, querySortBy }, { handleSort }] =
    useSortParams<keyof (typeof reportsFiltered)[number]>()

  const stringMatcher = (item: TaxAdvantageReport) => [
    item.employeeNo,
    item.benefitMobile,
    item.salaryDeductionAmount,
    item.benefitAmount,
    item.amount,
    item.vat,
  ]

  const { data: customers } = useQuery(adminQueries.adminReportCustomers(), () =>
    adminResources.getReportCustomers()
  )

  const selectedCustomerCountryId = customers?.find(
    customer => customer.id === filters.selectedCustomer
  )?.countryId
  const isNorwegianCustomer = selectedCustomerCountryId === NorwayId

  const { data: reports = [] } = useQuery(
    adminQueries.adminTaxAdvantageReports(filters),
    async () => {
      const reports = await adminResources.getTaxAdvantageReports(filters)
      setIsReportListLoading(false)
      return reports
    },
    {
      enabled: !filters.selectedCustomer || isNorwegianCustomer,
    }
  )
  const reportsFiltered = reports.filter(reportsObject =>
    getQueryFilter(searchFilter, stringMatcher)(reportsObject)
  )
  const sortedList = getSortedList({
    list: reportsFiltered,
    querySort,
    querySortBy,
    locale,
  })

  useEffect(() => {
    setCount(reports.length)
  }, [reports, setCount])

  const { visibleList, ListLoaderComponent } = useRenderLimit(sortedList, 30)
  const total = reportsFiltered.length
  const { formatNumber } = useFormattedNumberWithCurrency()

  if (filters.selectedCustomer && !isNorwegianCustomer) {
    return (
      <InfoBox emphasis="warning" className="text-center" marginTop={4} marginBottom={4}>
        <AlertIcon />
        <Translation id="Tax advantage report not applicable for non-norwegian companies" />
      </InfoBox>
    )
  }

  return (
    <>
      <ResultCount count={total} />
      {total > 0 ? (
        <Table>
          <thead>
            <tr>
              <Th onClick={handleSort} id="employeeNo">
                <Translation id="Employee No" />
              </Th>
              <Th onClick={handleSort} id="benefitMobile">
                <Translation id="Benefit Mobile" />
              </Th>
              <Th onClick={handleSort} id="salaryDeductionAmount" className="text-right">
                <Translation id="Salary Deduction Amount" />
              </Th>
              <Th onClick={handleSort} id="benefitAmount" className="text-right">
                <Translation id="Benefit Amount" />
              </Th>
              <Th onClick={handleSort} id="amount" className="text-right">
                <Translation id="Amount" />
              </Th>
              <Th onClick={handleSort} id="vat" className="text-right">
                <Translation id="Vat" />
              </Th>
            </tr>
          </thead>
          <tbody>
            {visibleList?.map(
              (
                { employeeNo, benefitMobile, salaryDeductionAmount, amount, benefitAmount, vat },
                index
              ) => (
                <tr key={`${employeeNo}-${index}`}>
                  <Td>
                    <Highlight text={employeeNo?.toString()} pattern={searchFilter} />
                  </Td>
                  <Td>
                    <Highlight text={benefitMobile} pattern={searchFilter} />
                  </Td>
                  <Td className="text-right">
                    <Highlight
                      text={formatNumber({ amount: Number(salaryDeductionAmount), precision: 2 })}
                      pattern={searchFilter}
                    />
                  </Td>
                  <Td className="text-right">
                    <Highlight
                      text={formatNumber({ amount: Number(benefitAmount), precision: 2 })}
                      pattern={searchFilter}
                    />
                  </Td>
                  <Td className="text-right">
                    <Highlight
                      text={formatNumber({ amount: Number(amount), precision: 2 })}
                      pattern={searchFilter}
                    />
                  </Td>
                  <Td className="text-right">
                    <Highlight
                      text={formatNumber({ amount: Number(vat), precision: 2 })}
                      pattern={searchFilter}
                    />
                  </Td>
                </tr>
              )
            )}
          </tbody>
        </Table>
      ) : (
        !isReportListLoading && <NoResults />
      )}
      <ListLoaderComponent />
    </>
  )
}
